import {isNullOrEmpty} from "@2d-front-monorepo/core";
import {logErrorToServer} from "@2d-front-monorepo/design-library";
import {HolderResult, InstallationLadingPage} from "@2d-front-monorepo/permits-business";
import {API_ENDPOINT} from "./api-endpoint";
import {REACT_APP_HOSTNAME} from "./config";
import {userStore} from "./contexts/UserContext";
import {getGlobalContext} from "./hooks/getGlobalContext";
import {getHttpClient} from "./hooks/getHttpClient";
import {currentLoadStatus, ReloadEntities} from "./hooks/reloadPermits";
import {PrivateRoutes} from "./routes";

export function isSafariModuleLoadError(error: unknown): boolean {
	return error instanceof Error && error.message === "Importing a module script failed.";
}

export function permitsLogErrorToServer(error: unknown): void {
	if (isSafariModuleLoadError(error)) return;
	logErrorToServer(`${API_ENDPOINT.errorLogging}?hostName=${REACT_APP_HOSTNAME}`, error);
}

// After a deployment, an old cached index.html requests hashed chunks that no longer
// exist -> "Failed to fetch dynamically imported module". Reload ONCE per session
// onto the fresh deploy. The sessionStorage guard prevents a reload loop when the
// module is genuinely broken rather than merely stale.
export function recoverFromStaleChunk(error: unknown): boolean {
	const msg = error instanceof Error ? error.message : String(error);
	if (!/Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Load failed/i.test(msg)) {
		return false;
	}
	const KEY = "staleChunkReloaded";
	if (sessionStorage.getItem(KEY)) return false; // already retried — treat as a real failure
	sessionStorage.setItem(KEY, "1");
	window.location.reload();
	return true;
}

export async function logOff(): Promise<void> {
	try {
		await getHttpClient().authentication.signOff();
	} catch {

	} finally {
		userStore.patchState(x => x.authStatus = "unauthorized");
	}
}

export function isProfileIncomplete(holder: HolderResult): boolean {
	return isNullOrEmpty(holder.givenName) || isNullOrEmpty(holder.familyName) || isNullOrEmpty(holder.cellPhone?.phone);
}

export function getDefaultLandingPage(): string {
	const globalConfig = getGlobalContext();

	switch (globalConfig.installation.configuration.ui.landingPage) {
		case InstallationLadingPage.CreatePermit:
			return PrivateRoutes.permitsHome;

		case InstallationLadingPage.PermitsList:
			return PrivateRoutes.permitsList;

		default: {
			// Boot status alone misroutes a returning user who signs in from a fresh session
			// (initialStatus is None before authentication): also honor what's loaded NOW.
			const status = globalConfig.initialStatus | currentLoadStatus();
			return (status & (ReloadEntities.Permits | ReloadEntities.Reservations)) !== 0
				? PrivateRoutes.permitsList
				: PrivateRoutes.permitsHome;
		}
	}
}
